import React, { FC, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Dropdown, Drawer } from 'antd';
import { UserOutlined, SettingOutlined, FileTextOutlined, BookOutlined, QuestionCircleOutlined, BarChartOutlined, KeyOutlined } from '@ant-design/icons';
import Logo from './svg/Logo';
import treeImage from '../assets/images/background/TreeWithBird.png';
import HomeIcon from './svg/HomeButton';
import AdminIcon from './svg/AdminIcon';  
import ShildIcon from './svg/ShildIcon';
import LogOutSmall from './svg/LogOutSmall';
import LogOutIcon from './svg/LogOut';
import ReloadIcon from './svg/ReloadIcon';
import ChangePasswordModal from './ChangePasswordModal';
import { subscriptionStatus, subscriptionExpiry, tokenName } from '../utils/data';
import axios from 'axios';
import { CreatePortalSessionUrl } from '../utils/network';
import { useGetChatbotSettingsQuery } from '../redux/api/chatbotApi';

interface HeaderProps {
  currentUser: {
    fullname: string;
    role: string;
    email: string;
  };
  showRefresh?: boolean;
  onRefresh?: () => void;
  showAdmin?: boolean;
}

const Header: FC<HeaderProps> = ({ currentUser, showRefresh, onRefresh, showAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const isAdmin = currentUser.role.toLowerCase() === 'admin';

  const { data: chatbotSettings } = useGetChatbotSettingsQuery();
  const settingsData = chatbotSettings ? (Array.isArray(chatbotSettings) ? chatbotSettings[0] : chatbotSettings) : null;
  const botName = settingsData?.name || 'Clara';

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const handleUpdateBilling = async () => {
    try {
      const token = localStorage.getItem(tokenName);
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(CreatePortalSessionUrl, {}, { headers });
      if (res.data && res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (error) {
      console.error("Error creating portal session:", error);
    }
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <style>{`
        /* Profile Dropdown */
        .profile-dropdown .ant-dropdown-menu {
          background: linear-gradient(white, white) padding-box,
                      linear-gradient(to bottom right, #4682B4, transparent 24%, transparent 73%, #4682B4) border-box !important;
          border: 2px solid transparent !important;
          border-radius: 17px !important;
          box-shadow: 0px 8px 14px 3px rgba(0,0,0,0.08) !important;
          padding: 24px 20px !important;
        }
        .profile-dropdown .ant-dropdown-menu-item {
          padding: 0 0 !important;
          border-radius: 0 !important;
          background: transparent !important;
        }
        .profile-dropdown .ant-dropdown-menu-item:hover {
          background: transparent !important;
        }
        .profile-dropdown .ant-dropdown-menu-item-divider {
          margin: 8px 0 !important;
          background-color: #1F2A3712 !important;
        }
      `}</style>

      {localStorage.getItem(subscriptionStatus) === 'past_due' && (
        <div className="bg-[#fff3cd] text-[#856404] px-5 md:px-[60px] py-2.5 text-center text-[13px] md:text-sm font-['Lexend',sans-serif] w-full border-b border-[#ffeeba] flex items-center justify-center">
          <span className="mr-2">⚠️ Your payment failed. You are in a grace period. Please update your billing details to prevent interruption.</span>
          <button onClick={handleUpdateBilling} className="font-semibold underline text-[#856404] hover:text-[#d39e00] bg-transparent border-none cursor-pointer p-0">Update Billing</button>
        </div>
      )}

      <header className="top-0 z-[100] pointer-events-auto px-5 md:px-[60px] pt-[33px] md:pt-[45px] pb-[10px] flex justify-between items-center flex-row w-full mx-auto">
        <Link 
          to="/" 
          className="logo-section cursor-pointer relative z-[101] pointer-events-auto block"
        >
          <Logo className="h-auto md:h-[46px] w-[140px] md:w-auto object-contain" />
        </Link>
        <div className="flex items-center justify-end w-auto gap-[12px] md:gap-5">
          
          {/* Refresh Button - Only visible on Case Management Page OR if showRefresh is true */}
          {showRefresh && (
            <button 
              className="flex items-center gap-0 md:gap-[8px] bg-white p-[2.5px] md:p-[4px_16px_4px_4px] rounded-full shadow-[0px_4px_12px_rgba(0,0,0,0.08)] cursor-pointer transition-all duration-200 border-none hover:-translate-y-[2px] hover:shadow-[0px_6px_16px_rgba(0,0,0,0.12)]" 
              onClick={onRefresh}
            >
              <span className="flex items-center justify-center">
                <ReloadIcon className="w-[30px] h-[30px] md:w-[28px] md:h-[28px]" />
              </span>
              <span className="hidden md:inline font-['Lexend',sans-serif] font-medium text-[14px] text-[#1F2A37]">Refresh</span>
            </button>
          )}

          {/* Home Button */}
          <Link 
            to="/" 
            className="flex items-center gap-0 md:gap-[8px] bg-white p-[2.5px] md:p-[4px_16px_4px_4px] rounded-full shadow-[0px_4px_12px_rgba(0,0,0,0.08)] cursor-pointer transition-all duration-200 border-none hover:-translate-y-[2px] hover:shadow-[0px_6px_16px_rgba(0,0,0,0.12)] no-underline relative z-[101] pointer-events-auto"
          >
            <span className="flex items-center justify-center">
              <HomeIcon className="w-[30px] h-[30px] md:w-[28px] md:h-[28px]" />
            </span>
            <span className="hidden md:inline font-['Lexend',sans-serif] font-medium text-[14px] text-[#1F2A37]">Home</span>
          </Link>


          {/* Admin Button - Only visible on Dashboard AND if user is admin */}
          {isAdmin && showAdmin && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'active-users',
                    label: (
                      <div className="flex items-center gap-2 py-1 font-['Lexend',sans-serif] text-[13px] font-medium text-[#1F2A37]">
                        <UserOutlined className="text-[#4682B4]" />
                        <span>Active Users</span>
                      </div>
                    ),
                    onClick: () => navigate('/users')
                  },
                  {
                    key: 'total-cases',
                    label: (
                      <div className="flex items-center gap-2 py-1 font-['Lexend',sans-serif] text-[13px] font-medium text-[#1F2A37]">
                        <FileTextOutlined className="text-[#4682B4]" />
                        <span>Total Cases</span>
                      </div>
                    ),
                    onClick: () => navigate('/cases')
                  },
                  {
                    key: 'scoring-method',
                    label: (
                      <div className="flex items-center gap-2 py-1 font-['Lexend',sans-serif] text-[13px] font-medium text-[#1F2A37]">
                        <BarChartOutlined className="text-[#4682B4]" />
                        <span>Scoring Method</span>
                      </div>
                    ),
                    onClick: () => navigate('/scoring-method')
                  },
                  {
                    key: 'references',
                    label: (
                      <div className="flex items-center gap-2 py-1 font-['Lexend',sans-serif] text-[13px] font-medium text-[#1F2A37]">
                        <BookOutlined className="text-[#4682B4]" />
                        <span>References</span>
                      </div>
                    ),
                    onClick: () => navigate('/references')
                  },
                  {
                    key: 'clara-settings',
                    label: (
                      <div className="flex items-center gap-2 py-1 font-['Lexend',sans-serif] text-[13px] font-medium text-[#1F2A37]">
                        <SettingOutlined className="text-[#4682B4]" />
                        <span>{botName} Settings</span>
                      </div>
                    ),
                    onClick: () => navigate('/chatbot-settings')
                  }
                ]
              }}
              placement="bottomRight"
            >
              <button className="flex items-center gap-0 md:gap-[8px] bg-white p-[2.5px] md:p-[4px_16px_4px_4px] rounded-full shadow-[0px_4px_12px_rgba(0,0,0,0.08)] cursor-pointer transition-all duration-200 border-none hover:-translate-y-[2px] hover:shadow-[0px_6px_16px_rgba(0,0,0,0.12)]">
                <span className="flex items-center justify-center">
                  <AdminIcon className="w-[30px] h-[30px] md:w-[28px] md:h-[28px]" />
                </span>
                <span className="hidden md:inline font-['Lexend',sans-serif] font-medium text-[14px] text-[#1F2A37]">Admin</span>
              </button>
            </Dropdown>
          )}


          <div className="md:hidden w-[35px] h-[35px] bg-[#fbd38d] text-[#744210] flex items-center justify-center rounded-full font-semibold text-[13px] shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer hover:opacity-90 transition-opacity border-[3px] border-white box-border" onClick={() => setDrawerVisible(true)}>
            {getUserInitials(currentUser.fullname)}
          </div>
          
          <div className="hidden md:block">
            <Dropdown
              trigger={['click']}
              overlayClassName="profile-dropdown"
              menu={{
                items: [
                  {
                    key: '1',
                    label: (
                       <div className="flex flex-col pointer-events-none min-w-[200px]">
                         <div className="flex items-center justify-between gap-2 mb-1">
                           <span className="font-['Lexend',sans-serif] text-[14px] font-medium text-[#1F2A37]">{currentUser.fullname}</span>
                            <span className="bg-gradient-to-b from-[#FED298] to-[#FFEFD8] text-[#B96E0C] text-[10px] px-[8px] py-[5px] rounded-[8px] flex items-center gap-[4px] font-normal font-['Lexend',sans-serif] border border-[#B96E0C40]">
                              <ShildIcon className="w-[10px] h-[11px]" />
                              {isAdmin ? 'Admin' : 'User'}
                            </span>
                         </div>
                         <span className="font-['Lexend',sans-serif] text-[12px] text-[#555D67]">{currentUser.email}</span>
                       </div>
                    )
                  },
                  ...(!isAdmin ? [
                    { type: 'divider' as const },
                    {
                      key: 'support',
                      onClick: () => navigate('/support'),
                      label: (
                         <div className="flex items-center justify-between w-full py-1 hover:text-[#4682B4] font-['Lexend',sans-serif] text-[12px] font-normal text-[#1F2A37] transition-colors">
                           <div className="flex items-center gap-8">
                              <div className="bg-[#D9D9D96B] p-[6px] rounded-[5px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                              <QuestionCircleOutlined className="text-[#4682B4] text-[12px]" />
                             </div>
                             <span>Help & Support</span>
                           </div>
                           <span className="text-[14px]">›</span>
                         </div>
                      )
                    }
                  ] : []),
                  { type: 'divider' },
                  {
                    key: 'change-password',
                    onClick: () => setChangePasswordVisible(true),
                    label: (
                       <div className="flex items-center justify-between w-full py-1 hover:text-[#4682B4] font-['Lexend',sans-serif] text-[12px] font-normal text-[#1F2A37] transition-colors">
                         <div className="flex items-center gap-8">
                            <div className="bg-[#D9D9D96B] p-[6px] rounded-[5px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                            <KeyOutlined className="text-[#4682B4] text-[12px]" />
                           </div>
                           <span>Change Password</span>
                         </div>
                         <span className="text-[14px]">›</span>
                       </div>
                    )
                  },
                  { type: 'divider' },
                  {
                    key: '2',
                    onClick: handleLogout,
                    label: (
                       <div className="flex items-center justify-between w-full py-1 hover:text-[#4682B4] font-['Lexend',sans-serif] text-[12px] font-normal text-[#1F2A37] transition-colors">
                         <div className="flex items-center gap-8">
                            <div className="bg-[#D9D9D96B] p-[6px] rounded-[5px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                            <LogOutSmall className="w-[10px] h-[12px]" />
                           </div>
                           <span>Sign Out</span>
                         </div>
                         <span className="text-[14px]">›</span>
                       </div>
                    )
                  }
                ]
              }}
              placement="bottomRight"
            >
              <div className="w-[36px] h-[36px] bg-gradient-to-b from-[#FED298] to-[#FFEFD8] text-[#744210] flex items-center justify-center rounded-full font-semibold text-sm shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer hover:opacity-90 transition-opacity border-[4px] border-white box-border">
                {getUserInitials(currentUser.fullname)}
              </div>
            </Dropdown>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Drawer */}
      <Drawer
        placement="right"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width="63.18%"
        styles={{ body: { padding: 0 } }}
        className="[&_.ant-drawer-content]:bg-white"
        zIndex={1000}
      >
        <div className="relative h-full w-full flex flex-col pl-[20px] pr-[26px] pt-[33px] overflow-hidden">
          {/* Close button */}
          <div className="mb-[36px]">
            <button className="bg-transparent border border-[#1F2A37]/60 rounded-full w-[26px] h-[26px] flex items-center justify-center p-0 cursor-pointer hover:bg-[#1F2A37]/5 transition-colors" onClick={() => setDrawerVisible(false)}>
              <span className="text-[#1F2A37]/60 font-medium text-[20px] leading-none mb-[3px]">‹</span>
            </button>
          </div>

          {/* User Info */}
          <div className="mb-[32px]">
            <div className="flex items-center justify-between mb-1">
              <span className="font-['Lexend',sans-serif] text-[14px] font-medium text-[#1F2A37] pr-2 break-words">{currentUser.fullname}</span>
              <span className="bg-gradient-to-b from-[#FED298] to-[#FFEFD8] text-[#B96E0C] text-[10px] px-[11px] py-[5px] rounded-[8px] flex items-center gap-[4px] font-normal font-['Lexend',sans-serif] shrink-0 border border-[#B96E0C40] shadow-[0px_0px_6px_0px_#B96E0C3D]">
                <ShildIcon />
                {isAdmin ? 'Admin' : 'User'}
              </span>
            </div>
            <span className="font-['Lexend',sans-serif] text-[12px] font-light text-[#555D67]">{currentUser.email}</span>
          </div>

          <div className="w-full h-[1.2px] bg-[#1F2A3712] mb-[25px]"></div>

          {/* Help & Support (Users Only) */}
          {!isAdmin && (
            <button className="flex items-center justify-between w-full bg-transparent border-none p-0 cursor-pointer group mb-[18px]" onClick={() => { setDrawerVisible(false); navigate('/support'); }}>
               <div className="flex items-center gap-[10px]">
                 <div className="bg-[#D9D9D96B] p-[6px] rounded-[5px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                   <QuestionCircleOutlined className="text-[#4682B4] text-[14px]" />
                 </div>
                 <span className="font-['Lexend',sans-serif] text-[12px] font-normal text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">Help & Support</span>
               </div>
               <span className="text-[18px] text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">›</span>
            </button>
          )}

          {/* Change Password */}
          <button className="flex items-center justify-between w-full bg-transparent border-none p-0 cursor-pointer group mb-[18px]" onClick={() => { setDrawerVisible(false); setChangePasswordVisible(true); }}>
             <div className="flex items-center gap-[10px]">
               <div className="bg-[#D9D9D96B] p-[6px] rounded-[5px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                 <KeyOutlined className="text-[#4682B4] text-[14px]" />
               </div>
               <span className="font-['Lexend',sans-serif] text-[12px] font-normal text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">Change Password</span>
             </div>
             <span className="text-[18px] text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">›</span>
          </button>

          {/* Sign Out */}
          <button className="flex items-center justify-between w-full bg-transparent border-none p-0 cursor-pointer group" onClick={handleLogout}>
             <div className="flex items-center gap-[10px]">
               <div className="bg-[#D9D9D96B] p-[6px] rounded-[5px] flex items-center justify-center group-hover:bg-[#e2e8f0] transition-colors">
                 <LogOutIcon className="w-[12px] h-[14px] text-[#1F2A37]" />
               </div>
               <span className="font-['Lexend',sans-serif] text-[12px] font-normal text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">Sign Out</span>
             </div>
             <span className="text-[18px] text-[#1F2A37] group-hover:text-[#4682B4] transition-colors">›</span>
          </button>

          {/* Bottom Tree Image */}
          <div className="absolute bottom-[0px] right-[0px] pointer-events-none z-0">
            <img src={treeImage} alt="" className="w-[260px] h-auto object-contain origin-bottom-right" />
          </div>
        </div>
      </Drawer>

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
      />
    </>
  );
};

export default Header;
